'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../api/client';

export type Subscription = {
  stripeSubscriptionId: string;
  planName: string;
  status: 'active' | 'trialing' | 'canceled' | 'past_due';
  currentPeriodStartedAt: string;
  currentPeriodEndedAt: string;
  cancelAtPeriodEnd: boolean;
  amountCents: number;
  seatCount: number;
  canceledAt: string | null;
};

export function useSubscription() {
  const {
    getToken,
    isLoaded,
    isSignedIn,
    orgId: clerkOrganizationId,
  } = useAuth();

  return useQuery<Subscription | null>({
    queryKey: ['subscription', clerkOrganizationId],
    enabled: isLoaded && isSignedIn === true && !!clerkOrganizationId,
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.GET(
        '/api/v1/billing/subscription',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (error || !response.ok || !data) {
        throw new Error('Failed to fetch subscription');
      }

      const sub = data.subscription;
      return {
        stripeSubscriptionId: sub.stripeSubscriptionId,
        planName: sub.planName,
        status: sub.status as Subscription['status'],
        currentPeriodStartedAt: sub.currentPeriodStartedAt,
        currentPeriodEndedAt: sub.currentPeriodEndedAt,
        cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
        amountCents: sub.amountCents,
        seatCount: sub.seatCount,
        canceledAt: sub.canceledAt ?? null,
      };
    },
    staleTime: 30000,
    refetchInterval: 60000,
    retry: false,
  });
}
